// ===========================================================================
// LOCAL DEBUG ONLY — gitignored, do NOT push.
// Floating panel to tune the aim path live. The path is a circle worked out from
// the drag, so there are only two numbers behind it:
//   length - how far the path runs, as a multiple of the Earth-start to
//     Mars-arrival distance. It doesn't change with the force; it just has to be
//     long enough that the path always leaves the system rather than stopping
//     somewhere that reads as a destination.
//   sweep max - how far round the path curves (degrees) at no force. Force
//     straightens it from there, down to a dead straight line at full force.
// Plus the two on the game that decide the timing:
//   ship speed - SVG units of path per second. This is what makes a straight shot
//     arrive before Mars does: shorter path, less time.
//   target radius - how big the circle on Mars is, in screen pixels. It's the only
//     tolerance there is: the launch counts when the ship's position at Mars's
//     arrival falls inside it. The game reads this off the capture indicator's own
//     size at press time, so a value set here only lasts as long as the preview.
// Previews are drawn at a few force levels, aimed the way a press with no
// sideways drag would, so the whole spread is visible at once. Green means that
// launch would meet Mars, blue means it's aimed through the window but mistimed.
// The destination ring shows the window itself.
//
// Self-activating: js/index.js picks every *Debug.js up on its own, so just load
// the page with ?trajectoryDebug. It reads the game off window.app.game.
// ===========================================================================

// Force levels (0 to 1) the preview draws a path for.
const PREVIEW_FORCES = [0, 0.25, 0.5, 0.75, 1];

// Draw one ghost path per force level, so the spread from "curls right round" to
// "dead straight" is visible at a glance.
function drawPreviews(game) {
  const solarSystem = game.solarSystem;
  const svg = document.querySelector("#trajectory-layer")?.ownerSVGElement;
  if (!svg) return;
  document.querySelector("#trajectory-preview")?.remove();

  const layer = document.createElementNS("http://www.w3.org/2000/svg", "g");
  layer.setAttribute("id", "trajectory-preview");
  const aim = solarSystem.aimVectorFor(0);
  // The panel can retune the circle, so measure against the ring as it's currently
  // set rather than against the indicator the game reads at press time.
  const tolSvg = solarSystem.destinationRadiusSvg();

  PREVIEW_FORCES.forEach((f) => {
    const geo = solarSystem.aimGeometry(aim.x, aim.y, f);
    const near = solarSystem.closestApproach(solarSystem.marsEndPoint(), geo);
    // Same two tests the game runs: does it go through the window, and would it be
    // there when Mars is.
    const aimed = near && near.distance <= tolSvg;
    // Where this launch would be when Mars arrives, which is the test the game runs
    // and the spot the rendezvous marker is drawn on.
    const meet = solarSystem.svgPointAlong(geo, game.rendezvousTravelDistance());
    const onTime = solarSystem.isInDestination(meet);
    const color = aimed && onTime ? "#3ddc84" : "#4ad9ff";

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", solarSystem.trajectoryPathD(geo));
    path.setAttribute("fill", "none");
    path.setAttribute("stroke", color);
    path.setAttribute("stroke-width", "2");
    path.setAttribute("stroke-opacity", `${0.3 + 0.6 * f}`);
    layer.appendChild(path);

    // Mark where the ship would be when Mars gets there, and label how early or
    // late that is against the moment it passes closest.
    const at = meet;
    if (at) {
      const dot = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      dot.setAttribute("cx", at.x);
      dot.setAttribute("cy", at.y);
      dot.setAttribute("r", "5");
      dot.setAttribute("fill", color);
      layer.appendChild(dot);

      const off = (near ? near.length : 0) / game.shipSpeed - game.marsArrivalSec();
      const label = document.createElementNS("http://www.w3.org/2000/svg", "text");
      label.setAttribute("x", at.x + 10);
      label.setAttribute("y", at.y - 8);
      label.setAttribute("fill", color);
      label.setAttribute("font-size", "16");
      label.textContent = `${off >= 0 ? "+" : ""}${off.toFixed(1)}s`;
      layer.appendChild(label);
    }
  });

  svg.appendChild(layer);
}

// Show the destination ring while tuning, so the previews can be read against the
// window they have to pass through.
function showDestination(solarSystem) {
  // The game hides this ring unless aim debug is on; this panel is here to tune
  // against it, so it forces it up regardless.
  solarSystem.showTargetRing = true;
  solarSystem.setDestinationRadius(solarSystem.destinationRadiusPx);
  solarSystem.showDestination(true);
}

function numberField(label, value, step, onInput) {
  const wrap = document.createElement("label");
  wrap.style.cssText = "display:flex;align-items:center;justify-content:space-between;gap:8px;margin:2px 0";
  const span = document.createElement("span");
  span.textContent = label;
  const input = document.createElement("input");
  input.type = "number";
  input.step = step;
  input.value = value;
  input.style.cssText = "width:90px;background:#111;color:#fff;border:1px solid #444;border-radius:4px;padding:2px 4px";
  input.addEventListener("input", onInput);
  wrap.append(span, input);
  return {wrap, input};
}

function buildPanel(game) {
  const solarSystem = game.solarSystem;
  const panel = document.createElement("div");
  panel.style.cssText =
    "position:fixed;top:12px;left:12px;z-index:99999;background:rgba(0,0,0,.85);" +
    "color:#fff;font:12px/1.4 monospace;padding:12px 14px;border-radius:8px;width:240px";

  const title = document.createElement("div");
  title.textContent = "Aim path debug";
  title.style.cssText = "font-weight:bold;margin-bottom:6px";
  panel.appendChild(title);

  // Shape lives on the solar system, timing lives on the game.
  const fields = {
    length: {on: solarSystem.aimArc, field: numberField("length", solarSystem.aimArc.length, "0.1", apply)},
    sweepMax: {on: solarSystem.aimArc, field: numberField("sweep max", solarSystem.aimArc.sweepMax, "10", apply)},
    shipSpeed: {on: game, field: numberField("ship speed", game.shipSpeed, "1", apply)},
    destinationRadiusPx: {on: solarSystem, field: numberField("target radius px", solarSystem.destinationRadiusPx, "5", apply)},
  };
  Object.values(fields).forEach((f) => panel.appendChild(f.field.wrap));

  function apply() {
    for (const key in fields) {
      const v = parseFloat(fields[key].field.input.value);
      if (!Number.isNaN(v)) fields[key].on[key] = v;
    }
    refresh();
  }

  const refresh = () => {
    showDestination(solarSystem);
    drawPreviews(game);
  };

  const buttons = document.createElement("div");
  buttons.style.cssText = "display:flex;gap:6px;margin-top:8px";

  const show = document.createElement("button");
  show.textContent = "Show paths";
  show.style.cssText = "flex:1;padding:4px;cursor:pointer";
  show.addEventListener("click", refresh); // re-draw after entering the game

  const log = document.createElement("button");
  log.textContent = "Log config";
  log.style.cssText = "flex:1;padding:4px;cursor:pointer";
  log.addEventListener("click", () => {
    // eslint-disable-next-line no-console
    console.log("aimArc:", JSON.stringify(solarSystem.aimArc, null, 2),
      "\nshipSpeed:", game.shipSpeed,
      "\ndestinationRadiusPx:", solarSystem.destinationRadiusPx);
  });

  buttons.append(show, log);
  panel.appendChild(buttons);

  document.body.appendChild(panel);
  refresh(); // show the previews straight away
}

function init() {
  if (!new URLSearchParams(window.location.search).has("trajectoryDebug")) return;
  const tryAttach = () => {
    const game = window.app && window.app.game;
    if (!game || !game.solarSystem) return setTimeout(tryAttach, 200);
    buildPanel(game);
  };
  tryAttach();
}

init();
